/**
 * Custom hook for fetching initiative statistics
 * @module hooks/use-initiative-stats
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getInitiativeStatistics } from '@/lib/api/initiatives';
import type { InitiativeStatistics } from '@/types/initiative';

export interface UseInitiativeStatsReturn {
  data: InitiativeStatistics | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useInitiativeStats(): UseInitiativeStatsReturn {
  const [data, setData] = useState<InitiativeStatistics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchInitiativeStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInitiativeStatistics();
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load initiative statistics');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitiativeStats();
  }, [fetchInitiativeStats]);

  return { data, loading, error, refetch: fetchInitiativeStats };
}
